import React from "react";
import { Box, Typography } from "@mui/material";
import BarChart from "../../BarChart";
import PieChart from "../../PieChart";
import AreaChart from "../../AreaChart";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import BusinessIcon from "@mui/icons-material/Business";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import SavingsIcon from "@mui/icons-material/Savings";
import metric1 from "../../../../assets/images/metric1.svg";
import metric2 from "../../../../assets/images/metric2.svg";
import metric3 from "../../../../assets/images/metric3.svg";
import metric4 from "../../../../assets/images/metric4.svg";
import styles from "./overviewDashboard.module.scss";

interface PrimaryCard {
  count: number | string;
  label: string;
  change?: string;
  changeType?: "positive" | "negative";
}

interface PrimaryCards {
  totalPRs: PrimaryCard;
  totalPOs: PrimaryCard;
  inProcessPRs: PrimaryCard;
  nonCompliancePRs: PrimaryCard;
}

interface SecondaryCards {
  successRate: number;
  defAvgDays: number;
  poValueTotal: number;
  singleSourcePRs: number;
  quoteRespTotal: number;
  savingsPct: number;
}

interface OverviewDashboardProps {
  primaryCards?: PrimaryCards;
  secondaryCards?: SecondaryCards;
  prByMonth?: any[];
  prByStrategy?: any[];
  delayedVsOpenVsClosed?: any[];
}

interface CardConfig {
  key: string;
  icon: string;
}

const OverviewDashboard: React.FC<OverviewDashboardProps> = ({
  primaryCards,
  secondaryCards,
  prByMonth = [],
  prByStrategy = [],
  delayedVsOpenVsClosed = [],
}) => {
  const primaryCardConfigs: CardConfig[] = [
    {
      key: "totalPRs",
      icon: metric4,
    },
    {
      key: "totalPOs",
      icon: metric1,
    },
    {
      key: "inProcessPRs",
      icon: metric2,
    },
    {
      key: "nonCompliancePRs",
      icon: metric3,
    },
  ];

  const secondaryCardConfigs = [
    {
      key: "successRate",
      label: "Success Rate",
      icon: <CheckCircleOutlineIcon sx={{ fontSize: 20, color: "#00d2aa" }} />,
      iconBg: "#e6faf5",
      suffix: "%",
    },
    {
      key: "defAvgDays",
      label: "Def Avg (Days)",
      icon: <AccessTimeIcon sx={{ fontSize: 20, color: "#5b6b9e" }} />,
      iconBg: "#f0f2f8",
    },
    {
      key: "poValueTotal",
      label: "PO value (total)",
      icon: <AttachMoneyIcon sx={{ fontSize: 20, color: "#00d2aa" }} />,
      iconBg: "#e6faf5",
      prefix: "$",
      formatter: (value: number) => {
        if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
        if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
        return value.toString();
      },
    },
    {
      key: "singleSourcePRs",
      label: "Single Source PRs",
      icon: <BusinessIcon sx={{ fontSize: 20, color: "#ffa94d" }} />,
      iconBg: "#fff4e6",
    },
    {
      key: "quoteRespTotal",
      label: "Quote Resp. (total)",
      icon: <TrendingUpIcon sx={{ fontSize: 20, color: "#5b6b9e" }} />,
      iconBg: "#f0f2f8",
      prefix: "$",
      formatter: (value: number) => {
        if (value >= 1000000) return `${(value / 1000000).toFixed(2)}M`;
        if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
        return value.toString();
      },
    },
    {
      key: "savingsPct",
      label: "Savings %",
      icon: <SavingsIcon sx={{ fontSize: 20, color: "#00d2aa" }} />,
      iconBg: "#e6faf5",
      suffix: "%",
    },
  ];

  const renderPrimaryCards = () => {
    if (!primaryCards) return null;

    return primaryCardConfigs.map((config, index) => {
      const card =
        primaryCards[config.key as keyof typeof primaryCards];
      if (!card) return null;

      return (
        <Box key={index} className={styles.metricCard}>
          <Box className={styles.metricIconWrapper}>
            <Box className={styles.metricIcon}>
              <img
                src={config.icon}
                alt={config.key}
                className={styles.metricImage}
              />
            </Box>
          </Box>
          <Box className={styles.metricContent}>
            <Typography variant="h3" className={styles.metricValue}>
              {card.count}
            </Typography>
            <Typography variant="body2" className={styles.metricLabel}>
              {card.label}
            </Typography>
            {card.change && (
              <Typography
                className={`${styles.metricChange} ${
                  card.changeType === "positive"
                    ? styles.positive
                    : card.changeType === "negative"
                    ? styles.negative
                    : ""
                }`}
              >
                {card.change}
              </Typography>
            )}
          </Box>
        </Box>
      );
    });
  };

  const renderSecondaryCards = () => {
    if (!secondaryCards) return null;

    return secondaryCardConfigs.map((config, index) => {
      const value =
        secondaryCards[config.key as keyof typeof secondaryCards];
      if (value === undefined || value === null) return null;

      const displayValue = config.formatter
        ? config.formatter(value as number)
        : value;

      return (
        <Box key={index} className={styles.secondaryMetricCard}>
          <Box
            className={styles.secondaryMetricIcon}
            sx={{ backgroundColor: config.iconBg }}
          >
            {config.icon}
          </Box>
          <Box className={styles.secondaryMetricContent}>
            <Typography variant="body2" className={styles.secondaryMetricLabel}>
              {config.label}
            </Typography>
            <Typography variant="h5" className={styles.secondaryMetricValue}>
              {config.prefix || ""}
              {displayValue}
              {config.suffix || ""}
            </Typography>
          </Box>
        </Box>
      );
    });
  };

  return (
    <>
      <Box className={styles.primaryMetrics}>{renderPrimaryCards()}</Box>

      <Box className={styles.secondaryMetrics}>{renderSecondaryCards()}</Box>

      <Box className={styles.chartsSection}>
        <BarChart
          title="PR by Month"
          data={prByMonth}
          xAxisKey="month"
          bars={[
            { dataKey: "closed", color: "#00d2aa", label: "Closed" },
            { dataKey: "open", color: "#ff6b6b", label: "Open" },
          ]}
        />
        <PieChart
          title="PR by Strategy"
          data={prByStrategy}
          nameKey="strategy"
          valueKey="value"
          showPercentage={true}
        />
      </Box>

      <Box className={styles.fullWidthChart}>
        <AreaChart
          title="Delayed vs Open vs Closed PRs"
          data={delayedVsOpenVsClosed}
          xAxisKey="month"
          areas={[
            { dataKey: "delayed", color: "#ff6b6b", label: "Delayed" },
            { dataKey: "open", color: "#5b6b9e", label: "Open" },
            { dataKey: "closed", color: "#00d2aa", label: "Closed" },
          ]}
        />
      </Box>
    </>
  );
};

export default OverviewDashboard;
